import React from "react"
import { Box, Typography } from "@mui/material"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import AppDialog, { AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader } from "./Dialog"


// Dialog that edits several records at once: icon + title + what it affects, the fields, and the actions on the right
export default function AppBulkEditDialog({ open, onClose, icon, title, subtitle, actions, children, maxWidth = 'md' }) {
	return <AppDialog open={open} onClose={onClose} maxWidth={maxWidth}>
		<AppDialogHeader icon={icon} title={title} subtitle={subtitle} onClose={onClose}/>
		<AppDialogBody>{children}</AppDialogBody>
		<AppDialogFooter>
			<AppDialogCancel onClick={onClose}/>
			{actions}
		</AppDialogFooter>
	</AppDialog>
}

const actionSx = {
	height: 36,
	px: 2,
	borderRadius: '8px',
	textTransform: 'none',
	fontSize: 14,
	fontWeight: 500,
	boxShadow: 'none',
	whiteSpace: 'nowrap',
	'&:hover': { boxShadow: 'none' },
}

// Footer actions: the main one in blue, a destructive one outlined in red
export function AppBulkEditAction({ danger = false, sx = {}, ...props }) {
	return <ButtonLoadingSeeker
		{...props}
		variant={danger ? 'outlined' : 'contained'}
		sx={danger
			? {
				...actionSx,
				color: dsColorNew.dangerRed,
				borderColor: dsColorNew.dangerBorder,
				backgroundColor: dsColorNew.white,
				'&:hover': { borderColor: dsColorNew.dangerRed, backgroundColor: dsColorNew.dangerBg },
				...sx,
			}
			: {
				...actionSx,
				backgroundColor: dsColorNew.appBlue,
				'&:hover': { backgroundColor: dsColorNew.appBlueHover, boxShadow: 'none' },
				...sx,
			}}
	/>
}

// Soft note above the fields: "info" (blue), "warning" (amber), "success" (green) or "error" (red)
export function AppNote({ severity = 'info', children, sx = {} }) {
	const tone = {
		warning: { color: dsColorNew.noteAmber, bg: dsColorNew.noteAmberBg, border: dsColorNew.noteAmberBorder, icon: 'triangle-exclamation' },
		success: { color: dsColorNew.successText, bg: dsColorNew.successBg, border: dsColorNew.successBar, icon: 'circle-check' },
		error: { color: dsColorNew.dangerRed, bg: dsColorNew.dangerBg, border: dsColorNew.dangerBorder, icon: 'circle-exclamation' },
	}[severity] || { color: dsColorNew.noteInfo, bg: dsColorNew.noteInfoBg, border: dsColorNew.noteInfoBorder, icon: 'circle-info' }

	return <Box role="note" sx={{
		display: 'flex', alignItems: 'flex-start', gap: 1.25,
		px: 1.5, py: 1.25, borderRadius: '10px',
		backgroundColor: tone.bg, border: `1px solid ${tone.border}`,
		color: tone.color, fontSize: 13, lineHeight: 1.45,
		...sx,
	}}>
		<IconSeeker icon={tone.icon} sx={{ fontSize: 14, mt: '2px', color: tone.color }}/>
		<Box>{children}</Box>
	</Box>
}

// The records a bulk edit touches, each with its current values: [{ key, name, content, aside }]
export function AppAffectedList({ label = 'Affected reports', hint, rows = [], sx = {} }) {
	return <Box sx={sx}>
		<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 2.5, mb: 1 }}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
			{!!hint && <Typography sx={{ fontSize: 11, fontWeight: 500, color: dsColorNew.textSubtle }}>{hint}</Typography>}
		</Box>
		<Box sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', maxHeight: 320, overflowY: 'auto' }}>
			{rows.map(({ key, name, content, aside }) => <Box key={key} sx={{
				display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 1.25,
				borderBottom: `1px solid ${dsColorNew.grayBorder}`,
				'&:last-of-type': { borderBottom: 0 },
			}}>
				<Typography sx={{ width: 190, flexShrink: 0, fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Typography>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, minWidth: 0 }}>{content}</Box>
				{!!aside && <Box sx={{ ml: 'auto', flexShrink: 0 }}>{aside}</Box>}
			</Box>)}
		</Box>
	</Box>
}

// A value shown as a soft chip; "empty" for the "none" text, "warning" for a status tag, "accent" for e-mails and links
export function AppValueChip({ children, tone = 'default', ...props }) {
	const tones = {
		default: { bg: dsColorNew.pillTrackBg, color: dsColorNew.textDark },
		empty: { bg: 'transparent', color: dsColorNew.textSubtle },
		warning: { bg: dsColorNew.noteAmberBg, color: dsColorNew.noteAmber },
		accent: { bg: dsColorNew.pillActiveBg, color: dsColorNew.appBlue },
	}
	const { bg, color } = tones[tone] || tones.default

	return <Box component="span" {...props} sx={{
		display: 'inline-flex', alignItems: 'center', gap: 0.6,
		px: tone === 'empty' ? 0 : 1, py: 0.4, borderRadius: '6px',
		backgroundColor: bg, color, fontSize: 12, fontWeight: tone === 'warning' ? 500 : 400, whiteSpace: 'nowrap',
		...props?.sx,
	}}>
		{children}
	</Box>
}
